import { z } from 'zod';
import { clientDashboardCarbonFootprintSchema } from './carbon.js';
import { listingStatusSchema, listingTypeSchema } from './listing.js';
import { marketplaceListingSchema } from './marketplace.js';

/**
 * Customer (seller) portal dashboard DTOs.
 *
 * Median-only: low/high FMV bands and confidence are ops-internal and are never
 * exposed to clients (same rule enforced in apps/api/src/routes/client/assets.ts).
 *
 * Backs:
 *   GET /api/client/dashboard/summary        → clientDashboardSummarySchema
 *   GET /api/client/dashboard/fmv-timeseries → clientFmvTimeseriesResponseSchema
 *
 * The summary aggregates top-level vAssetList rows (RLS-scoped to the caller's seller_org) plus the D8 component estimate;
 * the timeseries aggregates over v_client_portfolio_fmv_daily (514). The listings
 * block (RUF-311) aggregates the seller's OWN `listing` rows (RLS-scoped to its
 * customer_org).
 */

/** Per-lifecycle-state counts. Keys mirror the seeded asset_lifecycle_state ids. */
export const clientLifecycleCountsSchema = z.object({
  active: z.number().int(),
  idle: z.number().int(),
  inTransit: z.number().int(),
  decommissioned: z.number().int(),
  disposed: z.number().int(),
  quarantined: z.number().int(),
});
export type ClientLifecycleCounts = z.infer<typeof clientLifecycleCountsSchema>;

/** Per-condition-band counts. `unknown` folds in NULL condition_band. */
export const clientConditionCountsSchema = z.object({
  likeNew: z.number().int(),
  normalEnterpriseWear: z.number().int(),
  highWear: z.number().int(),
  unknown: z.number().int(),
});
export type ClientConditionCounts = z.infer<typeof clientConditionCountsSchema>;

/** One row per asset class in the seller's portfolio. */
export const clientDashboardByClassItemSchema = z.object({
  classId: z.string(),
  className: z.string(),
  // SIL-1648: units (sum of asset.quantity); lineCount is the number of inventory lines.
  count: z.number().int(),
  lineCount: z.number().int(),
  // Includes estFromComponentsUsd (D8).
  totalFmvMedUsd: z.coerce.number(),
  // Probe asset grouping D8: the part of totalFmvMedUsd that is the sum of
  // priced components of this class's servers that have no System FMV. 0 when none.
  estFromComponentsUsd: z.coerce.number(),
  lifecycle: clientLifecycleCountsSchema,
});
export type ClientDashboardByClassItem = z.infer<typeof clientDashboardByClassItemSchema>;

/**
 * One recent listing in the dashboard listings card. Median-only: carries the
 * seller's ask, never FMV low/high/confidence. `displayTitle` may be null for
 * older rows; the UI falls back to the listing type.
 */
export const clientDashboardRecentListingSchema = z.object({
  id: z.string(),
  listingType: listingTypeSchema,
  displayTitle: z.string().nullable(),
  askingPriceUsd: z.coerce.number(),
  currencyCode: z.string(),
  status: listingStatusSchema,
  createdAt: z.string(),
});
export type ClientDashboardRecentListing = z.infer<
  typeof clientDashboardRecentListingSchema
>;

/**
 * The seller's OWN listings summary (RUF-311). `activeCount` /
 * `totalActiveAskingUsd` cover only 'active' listings; `recent` is the newest
 * handful across ALL statuses, newest first.
 */
export const clientDashboardListingsSchema = z.object({
  activeCount: z.number().int(),
  totalActiveAskingUsd: z.coerce.number(),
  recent: z.array(clientDashboardRecentListingSchema),
});
export type ClientDashboardListings = z.infer<typeof clientDashboardListingsSchema>;

/** GET /api/client/dashboard/summary response. */
export const clientDashboardSummarySchema = z.object({
  // SIL-1648: units (sum of quantity); totalLineCount is the number of lines.
  totalAssetCount: z.number().int(),
  totalLineCount: z.number().int(),
  totalFmvMedUsd: z.coerce.number(),
  // SIL-519: assets onboarded into the portfolio that have never had an FMV
  // snapshot bootstrapped (latestFmvMedUsd IS NULL on vAssetList). These
  // assets contribute $0 to totalFmvMedUsd/v_client_portfolio_fmv_daily even
  // though they are fully owned — the UI surfaces this as a caveat next to
  // the FMV total so the number is never silently understated without
  // explanation.
  unpricedAssetCount: z.number().int(),
  // Probe asset grouping D8: servers with no System FMV contribute the sum of
  // their priced components. totalUsd is already inside totalFmvMedUsd;
  // assetCount is the number of such servers (no longer counted as unpriced).
  componentEstimate: z.object({
    totalUsd: z.coerce.number(),
    assetCount: z.number().int(),
  }),
  // True when totalFmvMedUsd includes a component estimate: the UI labels the
  // total "est. from components".
  totalFmvIncludesComponentEstimate: z.boolean(),
  lifecycle: clientLifecycleCountsSchema,
  condition: clientConditionCountsSchema,
  byClass: z.array(clientDashboardByClassItemSchema),
  listings: clientDashboardListingsSchema,
  // SIL-2504: assets with an inferred (unverified) SKU match are excluded from every valuation
  // total above (they stay in inventory). Lines and units, so the UI can say "N assets (M units) excluded".
  unverifiedSkuMatch: z.object({ lineCount: z.number().int(), unitCount: z.number().int() }).optional(),
  // SIL-189: portfolio embodied-carbon aggregate (v_sku_carbon_footprint_latest
  // joined to the RLS-scoped vAssetList). Bounds are sum-of-independent-bounds.
  carbonFootprint: clientDashboardCarbonFootprintSchema,
});
export type ClientDashboardSummary = z.infer<typeof clientDashboardSummarySchema>;

/**
 * One point in the portfolio FMV-over-time series. `day` is 'YYYY-MM-DD'.
 *
 * `newlyPricedAssetCount` (SIL-518) — how many assets in the portfolio had
 * their FMV-history-first-ever snapshot land on this day. The underlying
 * view (`v_client_portfolio_fmv_daily`, 724) folds an asset's whole first
 * value into that day's delta (no prior snapshot to diff against), which
 * reads as a value spike even though it's a data-history boundary, not a
 * real value event. 0 on every other day.
 */
export const clientFmvTimeseriesPointSchema = z.object({
  day: z.string(),
  totalFmvMedUsd: z.coerce.number(),
  newlyPricedAssetCount: z.number().int(),
});
export type ClientFmvTimeseriesPoint = z.infer<typeof clientFmvTimeseriesPointSchema>;

/** GET /api/client/dashboard/fmv-timeseries response. Oldest → newest. */
export const clientFmvTimeseriesResponseSchema = z.object({
  data: z.array(clientFmvTimeseriesPointSchema),
});
export type ClientFmvTimeseriesResponse = z.infer<typeof clientFmvTimeseriesResponseSchema>;

/**
 * SIL-2141: Market Pulse Overview modules (spec 2026-10-20-market-pulse-modules-design.md).
 * Listings here are the caller-projected marketplace cards.
 */
const fleetRef = { fleetModelName: z.string(), fleetUnits: z.number().int() };
export const marketPulseReasonSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('same_sku'), ...fleetRef }),
  z.object({ kind: z.literal('same_family'), ...fleetRef }),
  z.object({ kind: z.literal('same_generation'), generation: z.string(), ...fleetRef }),
  z.object({ kind: z.literal('new') }),
]);
export const marketPulseSchema = z.object({
  recommended: z.array(z.object({ listing: marketplaceListingSchema, reason: marketPulseReasonSchema })).max(3),
  discounts: z
    .array(
      z.object({
        listing: marketplaceListingSchema,
        fmvMedianUsd: z.number().nullable(),
        /** SIL-2297: SKU FMV band bounds (verified callers only, like fmvMedianUsd). Optional so older payloads parse. */
        fmvLowUsd: z.number().nullable().optional(),
        fmvHighUsd: z.number().nullable().optional(),
        pctVsFmv: z.number().nullable(),
        comps: z.number().int(),
      }),
    )
    .max(4),
  exchange: z.object({
    activeListings: z.number().int(),
    units: z.number().int(),
    skus: z.number().int(),
  }),
});
export type MarketPulseReason = z.infer<typeof marketPulseReasonSchema>;
export type MarketPulse = z.infer<typeof marketPulseSchema>;
